import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  MessageSquare, Send, User, Search, RefreshCw, Check, CheckCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function MessagingPanel({ userType = 'teacher', students = [] }) {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('faheem_token');
  const messagesContainerRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    if (token) {
      fetchConversations();
      fetchUnreadCount();
    }
  }, [token]);

  // Poll for new messages
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (selectedChat) {
        fetchMessages(selectedChat.id, false);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [token, selectedChat]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/conversations/`, {
        params: { access_token: token }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/unread-count/`, {
        params: { access_token: token }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchMessages = async (otherUserId, showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/`, {
        params: { access_token: token, other_user_id: otherUserId }
      });
      setMessages(response.data.messages || []);
      
      // Mark as read
      await axios.put(`${BACKEND_URL}/api/messages/mark-all-read/`, null, {
        params: { access_token: token, other_user_id: otherUserId }
      });
      fetchUnreadCount();
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChat = (user) => {
    setSelectedChat({
      id: user.other_user_id || user.id,
      name: user.other_user_name || user.username || user.email,
      avatar: user.other_user_avatar || user.avatar_url,
      role: user.other_user_role || 'student'
    });
    fetchMessages(user.other_user_id || user.id);
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedChat || sending) return;

    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/messages/send`, {
        recipient_id: selectedChat.id,
        content: newMessage.trim()
      }, {
        params: { access_token: token }
      });
      
      setNewMessage('');
      fetchMessages(selectedChat.id, false);
      toast.success('Message sent!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // Filter contacts based on search
  const filteredStudents = students.filter(s => 
    (s.username || s.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredConversations = conversations.filter(c =>
    (c.other_user_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 h-[600px] border border-slate-200 rounded-xl overflow-hidden bg-white shadow-lg" style={{ contain: 'layout' }}>
      {/* Left Panel - Contacts */}
      <div className="lg:col-span-1 border-r border-slate-200 flex flex-col bg-slate-50 overflow-hidden">
        {/* Search Header */}
        <div className="p-4 border-b border-slate-200 bg-white flex-shrink-0">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Messages
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-slate-100 border-0"
            />
          </div>
        </div>

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto">
          {/* Recent Conversations */}
          {filteredConversations.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-100">
                Recent Chats
              </p>
              {filteredConversations.map((conv) => (
                <div
                  key={conv.other_user_id}
                  onClick={() => handleSelectChat(conv)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-100 ${
                    selectedChat?.id === conv.other_user_id 
                      ? 'bg-blue-50 border-l-4 border-l-blue-600' 
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {conv.other_user_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-slate-800 truncate">{conv.other_user_name}</p>
                      {conv.unread_count > 0 && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {conv.last_message?.content || 'Start a conversation'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All Students/Teachers */}
          {userType === 'teacher' && filteredStudents.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-100">
                Your Students
              </p>
              {filteredStudents.map((student) => (
                <div
                  key={student.id}
                  onClick={() => handleSelectChat(student)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-100 ${
                    selectedChat?.id === student.id 
                      ? 'bg-blue-50 border-l-4 border-l-blue-600' 
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {student.username?.charAt(0).toUpperCase() || student.email?.charAt(0).toUpperCase() || 'S'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{student.username || 'Student'}</p>
                    <p className="text-sm text-slate-500 truncate">{student.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Teachers List (for students) */}
          {userType === 'student' && filteredStudents.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-100">
                Your Teachers
              </p>
              {filteredStudents.map((teacher) => (
                <div
                  key={teacher.id}
                  onClick={() => handleSelectChat(teacher)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-100 ${
                    selectedChat?.id === teacher.id 
                      ? 'bg-blue-50 border-l-4 border-l-blue-600' 
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {teacher.username?.charAt(0).toUpperCase() || teacher.email?.charAt(0).toUpperCase() || 'T'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{teacher.username || 'Teacher'}</p>
                    <p className="text-sm text-slate-500 truncate">{teacher.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredConversations.length === 0 && filteredStudents.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No contacts found</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Chat */}
      <div className="lg:col-span-2 flex flex-col bg-white overflow-hidden">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center gap-4 flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                {selectedChat.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-lg">{selectedChat.name}</h3>
                <p className="text-sm text-slate-500 capitalize">{selectedChat.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => fetchMessages(selectedChat.id)}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Messages Area */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-slate-50">
              {loading && messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No messages yet</p>
                    <p className="text-sm">Send a message to start the conversation</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      {/* Date Header */}
                      <div className="flex items-center justify-center mb-4">
                        <span className="bg-white px-4 py-1 rounded-full text-xs font-medium text-slate-500 shadow-sm border">
                          {date}
                        </span>
                      </div>
                      
                      {/* Messages for this date */}
                      <div className="space-y-3">
                        {msgs.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.is_own_message ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`flex items-end gap-2 max-w-[75%] ${msg.is_own_message ? 'flex-row-reverse' : ''}`}>
                              {/* Avatar */}
                              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold shadow ${
                                msg.is_own_message 
                                  ? 'bg-blue-600' 
                                  : 'bg-gradient-to-br from-green-500 to-teal-600'
                              }`}>
                                {msg.is_own_message ? 'You' : (msg.sender_name?.charAt(0).toUpperCase() || 'U')}
                              </div>
                              
                              {/* Message Bubble */}
                              <div
                                className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                                  msg.is_own_message
                                    ? 'bg-blue-600 text-white rounded-br-sm'
                                    : 'bg-white text-slate-800 rounded-bl-sm border border-slate-200'
                                }`}
                              >
                                {!msg.is_own_message && (
                                  <p className="text-xs font-semibold text-green-600 mb-1">
                                    {msg.sender_name}
                                  </p>
                                )}
                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                  {msg.content}
                                </p>
                                <div className={`flex items-center justify-end gap-1 mt-1 ${
                                  msg.is_own_message ? 'text-blue-200' : 'text-slate-400'
                                }`}>
                                  <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                                  {msg.is_own_message && (
                                    msg.is_read 
                                      ? <CheckCheck className="w-3.5 h-3.5" />
                                      : <Check className="w-3.5 h-3.5" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200 bg-white flex-shrink-0">
              <div className="flex gap-3 items-center">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-100 border-0 rounded-full px-5 py-2.5 focus:ring-2 focus:ring-blue-500"
                  disabled={sending}
                />
                <Button 
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="bg-blue-600 hover:bg-blue-700 rounded-full w-12 h-12 p-0 shadow-lg"
                >
                  {sending ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-slate-50">
            <div className="text-center text-slate-400">
              <MessageSquare className="w-20 h-20 mx-auto mb-4 opacity-20" />
              <p className="text-xl font-medium text-slate-600">Select a conversation</p>
              <p className="text-sm mt-1">Choose someone from the left to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
